/**
 * Notes Page - Cross-Hub Integration
 * 
 * View and manage notes from all jobs with threading and @mentions.
 */

import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { 
  StickyNote, 
  Search, 
  Plus,
  MessageSquare,
  Briefcase,
  Filter,
  Loader2,
  ChevronRight,
  Pin,
  User,
  Calendar,
} from 'lucide-react'
import { cn, formatRelativeTime, getInitials, getAvatarColor } from '../lib/utils'
import { notesApi, type Note } from '../lib/api'

interface NotesPageProps {
  showCreate?: boolean
}

export default function NotesPage({ showCreate = false }: NotesPageProps) {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [jobFilter, setJobFilter] = useState<number | null>(null)
  
  // Fetch notes
  const { data: notesData, isLoading } = useQuery({
    queryKey: ['teams-notes', jobFilter],
    queryFn: () => notesApi.list({ job_id: jobFilter ?? undefined, limit: 100 }),
  })
  
  const notes = notesData?.notes || []
  
  // Filter notes by search
  const filteredNotes = useMemo(() => {
    if (!searchQuery) return notes
    const q = searchQuery.toLowerCase()
    return notes.filter(n => 
      n.text.toLowerCase().includes(q) ||
      n.job_name?.toLowerCase().includes(q) ||
      n.created_by?.name?.toLowerCase().includes(q)
    )
  }, [notes, searchQuery])
  
  // Group by pinned
  const pinnedNotes = filteredNotes.filter(n => n.is_pinned)
  const regularNotes = filteredNotes.filter(n => !n.is_pinned)

  return (
    <div className="h-full bg-slate-50 dark:bg-slate-900">
      <div className="max-w-4xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">Notes</h1>
            <p className="text-slate-500 dark:text-slate-400">
              {notes.length} notes across all jobs
            </p>
          </div>
          <button
            onClick={() => navigate('/notes/new')}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Note
          </button>
        </div>
        
        {/* Search & Filter */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search notes..."
              className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
          </div>
          <button className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
            <Filter className="w-4 h-4" />
            Filter
          </button>
        </div>
        
        {/* Notes List */}
        {isLoading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-violet-500 mb-3" />
            <p className="text-sm text-slate-400">Loading notes...</p>
          </div>
        ) : filteredNotes.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
            <StickyNote className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <p className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">No notes found</p>
            <p className="text-sm text-slate-400 mb-4">
              {searchQuery ? 'Try a different search' : 'Notes from jobs will appear here'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Pinned Notes */}
            {pinnedNotes.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-1 flex items-center gap-1">
                  <Pin className="w-3 h-3" />
                  Pinned
                </h3>
                <div className="space-y-3">
                  {pinnedNotes.map((note) => (
                    <NoteCard key={note.id} note={note} />
                  ))}
                </div>
              </div>
            )}
            
            {/* Regular Notes */}
            <div>
              {pinnedNotes.length > 0 && (
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-1">
                  Recent
                </h3>
              )}
              <div className="space-y-3">
                {regularNotes.map((note) => (
                  <NoteCard key={note.id} note={note} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Note Card Component
function NoteCard({ note }: { note: Note }) {
  const navigate = useNavigate()
  
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-md transition-shadow">
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div className={cn(
            "w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold bg-gradient-to-br flex-shrink-0",
            getAvatarColor(note.created_by?.name || '')
          )}>
            {getInitials(note.created_by?.name || '', note.created_by?.email)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm text-slate-800 dark:text-white">
                {note.created_by?.name || 'Unknown'}
              </span>
              {note.is_pinned && (
                <Pin className="w-3 h-3 text-amber-500" />
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>{formatRelativeTime(note.created_at)}</span>
              {note.job_name && (
                <>
                  <span>•</span>
                  <button 
                    onClick={() => { window.location.href = `https://dashboard.quotech.ai/jobs/${note.job_id}` }}
                    className="flex items-center gap-1 hover:text-violet-600"
                  >
                    <Briefcase className="w-3 h-3" />
                    {note.job_name}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Content */}
        <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap line-clamp-4">
          {note.text}
        </p>
        
        {/* Tags */}
        {note.pagename && (
          <div className="mt-3">
            <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
              {note.pagename}
            </span>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 dark:bg-slate-900 border-t border-slate-100 dark:border-slate-700">
        <div className="flex items-center gap-4 text-xs text-slate-500">
          {note.thread_count > 0 && (
            <span className="flex items-center gap-1">
              <MessageSquare className="w-3 h-3" />
              {note.thread_count} {note.thread_count === 1 ? 'reply' : 'replies'}
            </span>
          )}
          {note.person_responsible && (
            <span className="flex items-center gap-1">
              <User className="w-3 h-3" />
              {note.person_responsible}
            </span>
          )}
        </div>
        <button className="text-xs text-violet-600 hover:text-violet-700 font-medium flex items-center gap-1">
          View
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  )
}
